import { useRef, useState, useCallback } from 'react';
import { LiveAvatarSession, SessionEvent, SessionState } from '@heygen/liveavatar-web-sdk';
import api from '../services/api';

/**
 * Voice → gender lookup. Kept intact (Storm matches it server-side and
 * future male-voice support could resurface) but the customer UI now
 * filters to FEMALE_VOICES only — single-avatar deployment, female-only,
 * operator-configured via the backend's LIVEAVATAR_AVATAR_ID env var.
 * Aoede is the canonical default voice.
 */
const VOICE_GENDER_MAP: Record<string, string> = {
  Puck: 'Male', Charon: 'Male', Kore: 'Female', Fenrir: 'Male',
  Aoede: 'Female', Leda: 'Female', Orus: 'Male', Zephyr: 'Female',
  Callirrhoe: 'Female', Autonoe: 'Female', Enceladus: 'Male',
  Iapetus: 'Male', Umbriel: 'Male', Algieba: 'Male',
  Despina: 'Female', Erinome: 'Female', Algenib: 'Male',
  Rasalgethi: 'Male', Laomedeia: 'Female', Achernar: 'Female',
  Alnilam: 'Male', Schedar: 'Male', Gacrux: 'Female',
  Pulcherrima: 'Female', Achird: 'Male', Zubenelgenubi: 'Male',
  Vindemiatrix: 'Female', Sadachbia: 'Male', Sadaltager: 'Male',
  Sulafat: 'Female',
};

/**
 * Female voices only (14). Source-of-truth for every customer-facing
 * voice picker for the current single-avatar deployment. Default
 * selection across the app is Aoede (Storm migrates existing
 * male-voice agents to Aoede server-side; UI fallback in
 * `getVoiceGender` defaults unknown voices to Female so a transient
 * male voice still renders without crashing the active avatar render).
 */
export const FEMALE_VOICES: string[] = Object.entries(VOICE_GENDER_MAP)
  .filter(([, gender]) => gender === 'Female')
  .map(([name]) => name);

export const DEFAULT_FEMALE_VOICE = 'Aoede';

export function getVoiceGender(voiceName: string): string {
  return VOICE_GENDER_MAP[voiceName] || 'Female';
}

function int16ToBase64(samples: Int16Array): string {
  const bytes = new Uint8Array(samples.buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToInt16(base64: string): Int16Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Int16Array(bytes.buffer);
}

// Safety valve: flush if buffer exceeds 5 seconds of audio
const MAX_BUFFER_SAMPLES = 120000; // 5s at 24kHz

// Plan-LiveAvatarLifecycleAndCreditsFallback §B.2.1 — `platform_credits_exhausted`
// added 2026-05-13 for the case where Prodagi's master HeyGen account
// itself has run out of credits. Backend (Storm) emits this gate with
// the same 403 + `{code, message}` shape as the per-workspace gates;
// FE handles it via the existing fallback-to-plain-voice path. The
// `LiveAvatarGateModal` skip-renders this code (the user can't fix it).
export type LiveAvatarGateCode =
  | 'no_addon'
  | 'minutes_exhausted'
  | 'concurrency_capped'
  | 'platform_credits_exhausted';

export interface LiveAvatarGate {
  code: LiveAvatarGateCode;
  message: string;
}

export interface UseLiveAvatarReturn {
  isConnected: boolean;
  isConnecting: boolean;
  avatarGender: string;
  videoRef: React.RefObject<HTMLVideoElement>;
  connect: (agentId?: string, conversationId?: string) => Promise<void>;
  disconnect: () => void;
  sendAudio: (pcmBase64: string) => void;
  error: string | null;
  gate: LiveAvatarGate | null;
  clearGate: () => void;
}

export function useLiveAvatar(): UseLiveAvatarReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gate, setGate] = useState<LiveAvatarGate | null>(null);
  const [avatarGender, setAvatarGender] = useState<string>('Female');

  const clearGate = useCallback(() => setGate(null), []);

  const videoRef = useRef<HTMLVideoElement>(null!);
  const sessionRef = useRef<LiveAvatarSession | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const bufferRef = useRef<Int16Array[]>([]);
  const bufferLenRef = useRef(0);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Plan-LiveAvatarLifecycleAndCreditsFallback §A.2.5 — idempotency
  // guard. `disconnect()` is now called from multiple paths (stopSession,
  // WS structured-error frame, ws.onclose, ws.onerror, beforeunload,
  // conversation_ended WS frame), and any two of them can fire in quick
  // succession. Re-entering after `sessionRef` was already nulled out
  // would still issue a redundant `/avatar-session/stop` POST + try to
  // double-stop the SDK; the ref short-circuits that.
  const disconnectedRef = useRef<boolean>(true);

  const stopBackendSession = async (sid: string) => {
    try {
      await api.post('/api/voice/avatar-session/stop', { session_id: sid });
    } catch { /* best effort */ }
  };

  const flushBuffer = useCallback(() => {
    const session = sessionRef.current;
    if (!session || session.state !== SessionState.CONNECTED) return;

    const total = bufferLenRef.current;
    if (total === 0) return;

    const merged = new Int16Array(total);
    let offset = 0;
    for (const chunk of bufferRef.current) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    bufferRef.current = [];
    bufferLenRef.current = 0;

    try {
      console.log('[LiveAvatar] Flushing buffer, samples:', total, 'calling repeatAudio');
      session.repeatAudio(int16ToBase64(merged));
    } catch (err) {
      console.error('[LiveAvatar] Error sending audio:', err);
    }
  }, []);

  const connect = useCallback(async (agentId?: string, conversationId?: string) => {
    try {
      setIsConnecting(true);
      setError(null);
      setGate(null);
      // A new connect attempt opens the disconnect-allowed window again.
      disconnectedRef.current = false;

      // Get session token from backend.
      //
      // Task #17 Bug B follow-up — the OPTIONAL `conversation_id` query
      // param lets the backend register a Redis mapping
      // `voice:conv:<conv>:heygen_session = <heygen_session_id>` so that
      // server-side teardown (`_release_liveavatar_for_conversation`)
      // can resolve the HeyGen session id when the voice WS closes.
      // Without it, BE-initiated stops 404 against HeyGen REST and the
      // vendor room stays up → credits burn. Standalone avatar callers
      // (AgentAvatarPage test) omit it; they don't have a voice WS so
      // there's nothing to map, and BE just no-ops as before.
      const params = new URLSearchParams();
      if (agentId) params.set('agent_id', agentId);
      if (conversationId) params.set('conversation_id', conversationId);
      const qs = params.toString();
      const url = qs ? `/api/voice/avatar-session?${qs}` : '/api/voice/avatar-session';
      const res = await api.post<{
        session_token: string;
        livekit_url: string;
        livekit_client_token: string;
        ws_url: string;
        session_id: string;
        avatar_gender: string;
      }>(url);

      const { session_token, session_id, avatar_gender } = res.data;
      sessionIdRef.current = session_id;
      setAvatarGender(avatar_gender || 'Female');

      if (!session_token) {
        throw new Error('AI Avatar session returned no token');
      }

      console.log('[LiveAvatar] Creating session with SDK...');

      // Create SDK session — voiceChat: false because we handle audio ourselves
      const session = new LiveAvatarSession(session_token, {
        voiceChat: false,
      });
      sessionRef.current = session;

      // Log state changes
      session.on(SessionEvent.SESSION_STATE_CHANGED, (state: SessionState) => {
        console.log('[LiveAvatar] State changed:', state);
        if (state === SessionState.DISCONNECTED) {
          setIsConnected(false);
        }
      });

      // Attach video when stream is ready
      session.on(SessionEvent.SESSION_STREAM_READY, () => {
        console.log('[LiveAvatar] Stream ready, attaching video...');
        if (videoRef.current) {
          session.attach(videoRef.current);
          console.log('[LiveAvatar] Video attached');
        }
      });

      session.on(SessionEvent.SESSION_DISCONNECTED, (reason) => {
        console.log('[LiveAvatar] Disconnected, reason:', reason);
        setIsConnected(false);
      });

      console.log('[LiveAvatar] Starting session...');
      await session.start();
      console.log('[LiveAvatar] Session started, state:', session.state);

      setIsConnected(true);
      setIsConnecting(false);
    } catch (err: any) {
      console.error('[LiveAvatar] Connect error:', err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403 && detail && typeof detail === 'object' && detail.code) {
        const code = detail.code as LiveAvatarGateCode;
        const message = detail.message || 'AI Avatar is not available';
        setGate({ code, message });
        setError(message);
      } else {
        setError(err?.message || 'Failed to connect AI Avatar');
      }
      setIsConnecting(false);
      // Cleanup
      try { await sessionRef.current?.stop(); } catch {}
      sessionRef.current = null;
      if (sessionIdRef.current) {
        stopBackendSession(sessionIdRef.current);
        sessionIdRef.current = null;
      }
    }
  }, []);

  const disconnect = useCallback(() => {
    // Plan-LiveAvatarLifecycleAndCreditsFallback §A.2.5 — idempotency.
    // The new teardown wiring fires `disconnect()` from every WS exit
    // path (close/error/structured-error/conversation_ended/beforeunload)
    // PLUS the existing stopSession path. Without this guard a double-
    // call double-issues `/avatar-session/stop` and double-tries the
    // SDK stop, which logs a noisy SDK warning even though it's safe.
    if (disconnectedRef.current) return;
    disconnectedRef.current = true;

    if (sessionIdRef.current) {
      stopBackendSession(sessionIdRef.current);
      sessionIdRef.current = null;
    }

    // Clear debounce timer and flush remaining buffer before stopping
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    flushBuffer();

    try { sessionRef.current?.stop(); } catch {}
    sessionRef.current = null;

    bufferRef.current = [];
    bufferLenRef.current = 0;

    setIsConnected(false);
    setIsConnecting(false);
    setError(null);
  }, [flushBuffer]);

  const sendAudio = useCallback((pcmBase64: string) => {
    if (!sessionRef.current || sessionRef.current.state !== SessionState.CONNECTED) return;

    const pcm24k = base64ToInt16(pcmBase64);
    bufferRef.current.push(pcm24k);
    bufferLenRef.current += pcm24k.length;

    // Clear any pending debounce timer
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }

    // Force flush if buffer is very large (5s safety valve)
    if (bufferLenRef.current >= MAX_BUFFER_SAMPLES) {
      flushBuffer();
      return;
    }

    // Debounce: flush 300ms after last audio chunk (Gemini paused)
    flushTimerRef.current = setTimeout(() => {
      flushBuffer();
      flushTimerRef.current = null;
    }, 300);
  }, [flushBuffer]);

  return {
    isConnected,
    isConnecting,
    avatarGender,
    videoRef,
    connect,
    disconnect,
    sendAudio,
    error,
    gate,
    clearGate,
  };
}
